import { Fragment } from "react";

import { Divider, Flex, Icon, Overflow, Text, TooltipWrapper } from "@theorgchartdev/storybook";
import { CommentPopover } from "@/appComponents/Navigation/RightSidebar/Planning/Activity/CommentPopover";
import {
  enumObjectTypes,
  enumOverrideType,
  kDesiredHolders,
  kPositionStatus,
  PositionStatus,
  enumObjClassNames,
  kEmployeePositionID,
  kBlockedFieldTag
} from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import {
  enumChangeText,
  enumPositionChangeText,
  GetShortObjLabel,
  isOrphanChange,
  getRawPreviousFieldValue,
  getFormattedOriginalValue,
  resolveGroupPrior,
  CheckForPositionStatusChange,
  CheckForEmployeeAssignmentChange,
  getFilteredRows,
  filterUnchangedRows,
  FilterDisplayRowsOnAssignmentChange,
  FilterDisplayRowsOnPosStatusChange,
  hasParentChanged,
  enumAltChangeText
} from "./ChangeDisplayRow.utils";
import { RecordLink } from "./RecordLink";

const isNumericValue = val => typeof val === "string" && /\d/.test(val) && !/[a-zA-Z]/.test(val);

const NoBreak = ({ children }) => <span style={{ whiteSpace: "nowrap" }}>{children}</span>;

const OverflowTooltipWrapper = ({ tooltipProps, children }) => (
  <Overflow>
    {({ ref, isOverflowed }) => (
      <TooltipWrapper tooltipProps={{ ...tooltipProps, disabled: !isOverflowed }}>
        {children(ref)}
      </TooltipWrapper>
    )}
  </Overflow>
);

export const ChangeDisplayRow = ({
  change,
  currentParentDBObj,
  originalParentDBObj,
  showComment = false,
  tableView = false,
  onOverflow = undefined,
  showCompact = false,
  allChanges,
  doc,
  wsMgr
}) => {
  const objDB = doc.GetObjDB();

  const override = change.fParentOverride;
  const objType = override?.SmartGetFieldValue("objType") ?? enumObjectTypes.holder;
  const changeType = override?.SmartGetFieldValue("Type");

  const ctx = { override, change, wsMgr, objType, doc, tableView, objDB };

  const rows = (() => {
    const displayRows = change.GetDisplayRows();

    const showsMultipleRows = [
      enumOverrideType.add,
      enumOverrideType.move,
      enumOverrideType.change
    ].includes(changeType);

    if (showsMultipleRows && showCompact) {
      const effectiveRowCount = filterUnchangedRows(getFilteredRows(displayRows), ctx).length;
      if (effectiveRowCount > 1) {
        onOverflow?.(true);
        return displayRows.slice(0, 1);
      }
    }

    onOverflow?.(false);
    return displayRows;
  })();

  const hierarchyClass = doc?.GetHierarchyClass();
  const parentIDField = hierarchyClass?.GetParentIDFieldDef();
  const parentIDFieldName = parentIDField?.GetFieldName();
  const containerPositionFieldName = objDB
    ?.GetClassByName(enumObjClassNames.box)
    ?.GetParentIDFieldDef()
    ?.GetFieldName();

  const renderFilteredRows = filteredRows =>
    filteredRows.map(({ fieldDef, displayValue }, index, array) =>
      GetChangesDisplayContent(fieldDef, displayValue, index, array)
    );

  const GetDeleteContent = parentDBObj => {
    if (doc?.IsPlanChart()) {
      return GetChangedReportsContent(parentDBObj);
    }

    const changeText = appstr("Entity deleted", { entity: appstr("Record") });

    return (
      <OverflowTooltipWrapper tooltipProps={{ message: changeText }}>
        {ref => (
          <Flex ref={ref} gap={"2px"} alignItems={"flex-end"} className={"oc-text-truncate--2"}>
            <Text size={"xsmall"}>{changeText}</Text>
          </Flex>
        )}
      </OverflowTooltipWrapper>
    );
  };

  const GetPositionStatusChangeContentForPlanChart = () => {
    const filteredRows = FilterDisplayRowsOnPosStatusChange({
      rows,
      doc,
      tableView,
      change,
      wsMgr,
      objType
    });
    const { isChange } = CheckForPositionStatusChange({ doc, tableView, change, wsMgr, objType });

    return (
      <Flex direction={"column"} gap={"4px"}>
        {filteredRows.map(({ fieldDef, displayValue, value }, index, array) => {
          const showDivider = filteredRows.length > 1 && index < filteredRows.length - 1;

          if (isChange && fieldDef.GetFieldID() === kPositionStatus) {
            const fieldName = fieldDef?.GetFieldName();
            const isClose = value === PositionStatus.Closed;
            return (
              <Fragment key={fieldName}>
                {GetPositionStatusChangeContent(isClose, currentParentDBObj)}
                {showDivider && <Divider />}
              </Fragment>
            );
          }

          return GetChangesDisplayContent(fieldDef, displayValue, index, array, showDivider);
        })}
      </Flex>
    );
  };

  const GetEmployeeAssignmentChangeContentForPlanChart = () => {
    const { isAssigned, isUnassigned, isReleased, newPositionID } =
      CheckForEmployeeAssignmentChange(ctx);

    if (!isAssigned && !isUnassigned && !isReleased) {
      return null;
    }

    const filteredRows = FilterDisplayRowsOnAssignmentChange(
      filterUnchangedRows(getFilteredRows(rows), ctx)
    );

    const posCache = wsMgr?.fPlanManager?.fPositionCloneBaseCache;
    const newPositionDbObj = posCache?.[newPositionID];
    const boxClass = objDB?.GetClassByName(enumObjClassNames.box);
    const empPosFieldDef = boxClass?.GetFieldDefByID(kEmployeePositionID);

    const resolvedOriginalPosition = (() => {
      if (originalParentDBObj) return originalParentDBObj;
      const prevPosID = empPosFieldDef ? getRawPreviousFieldValue(empPosFieldDef, ctx) : null;
      return prevPosID ? (posCache?.[prevPosID] ?? null) : null;
    })();

    if (isAssigned) {
      const previousPositionID = empPosFieldDef
        ? getRawPreviousFieldValue(empPosFieldDef, ctx)
        : null;

      if (previousPositionID) {
        const previousPositionDbObj = posCache?.[previousPositionID];

        if (previousPositionDbObj && newPositionDbObj) {
          return (
            <Flex direction={"column"} gap={"4px"}>
              <Flex gap={"2px"} alignItems={"flex-end"} className={"oc-text-truncate--2"}>
                <Text size={"xsmall"}>{appstr("Reassigned from")}</Text>
                <RecordLink dbObj={previousPositionDbObj} docRef={doc} />
                <Text size={"xsmall"}>{appstr("to")}</Text>
                <RecordLink dbObj={newPositionDbObj} docRef={doc} />
              </Flex>
              {filteredRows.length > 0 && <Divider />}
              {renderFilteredRows(filteredRows)}
            </Flex>
          );
        }
      }
    }

    let assignmentText;
    let targetDbObj;

    if (isAssigned) {
      assignmentText = appstr("Assigned to");
      targetDbObj = newPositionDbObj;
    } else if (isUnassigned) {
      assignmentText = resolvedOriginalPosition ? appstr("Unassigned from") : appstr("Unassigned");
      targetDbObj = resolvedOriginalPosition;
    } else if (isReleased) {
      assignmentText = resolvedOriginalPosition ? appstr("Released from") : appstr("Released");
      targetDbObj = resolvedOriginalPosition;
    }

    return (
      <Flex direction={"column"} gap={"4px"}>
        <Flex alignItems={"flex-end"} className={"oc-text-truncate--2"}>
          <Text size={"xsmall"}>{assignmentText} </Text>
          {targetDbObj ? (
            <RecordLink dbObj={targetDbObj} docRef={doc} />
          ) : newPositionID ? (
            <Text size={"xsmall"} modifier="bold">
              {newPositionID}
            </Text>
          ) : null}
        </Flex>
        {filteredRows.length > 0 && <Divider />}
        {renderFilteredRows(filteredRows)}
      </Flex>
    );
  };

  const GetChangedReportsContent = parentDBObj => {
    let omitParentDBObjTag = false;
    const isHolderInPosChart = doc.IsPositionDocument() && objType === enumObjectTypes.holder;
    if (parentDBObj == null) {
      const dbObj = change.fDBObj;

      // For holders, we use the parent position as the parent element.
      if (isHolderInPosChart) {
        parentDBObj = dbObj?.GetParentPosition();
        if (!parentDBObj) {
          omitParentDBObjTag = true;
        }
      }
    }

    // chartRefObj covers Delete overrides where fDelta is also empty.
    const chartRefObj = change.fParentOverride?.SmartGetFieldValue("chartRefObj");
    const fallbackParentID =
      !parentDBObj &&
      (change.GetSupervisorID(true) ||
        (tableView &&
          (chartRefObj?.[containerPositionFieldName] || chartRefObj?.[parentIDFieldName])) ||
        change.fDelta?.[containerPositionFieldName] ||
        change.fDelta?.[parentIDFieldName]);

    const parentLabel = GetShortObjLabel(parentDBObj);

    let changeText = appstr("Reports to");
    if (doc.IsPlanChart() || doc.IsCustomChart() || tableView) {
      if (doc.IsPositionDocument()) {
        changeText = appstr(enumPositionChangeText[objType][changeType]);
        if (omitParentDBObjTag && !fallbackParentID) {
          changeText = appstr(enumAltChangeText[changeType]);
        }
      } else {
        changeText = appstr(enumChangeText[changeType]);
      }
    }

    if (
      tableView &&
      changeType === enumOverrideType.add &&
      !parentDBObj &&
      (fallbackParentID == "" || fallbackParentID == null)
    ) {
      changeText = appstr(enumAltChangeText[changeType]);
    }

    return (
      <OverflowTooltipWrapper
        tooltipProps={{ message: changeText + " " + (parentLabel || fallbackParentID || "") }}
      >
        {ref => (
          <Flex ref={ref} gap={"2px"} alignItems={"flex-end"} className={"oc-text-truncate--2"}>
            <Text size={"xsmall"}>{changeText}</Text>
            {parentDBObj ? (
              <RecordLink dbObj={parentDBObj} docRef={doc} />
            ) : fallbackParentID ? (
              <Text size={"xsmall"} modifier="bold">
                {" "}
                {fallbackParentID}
              </Text>
            ) : null}
          </Flex>
        )}
      </OverflowTooltipWrapper>
    );
  };

  const GetMoveContents = () => {
    const prevParent = GetShortObjLabel(originalParentDBObj);
    let curLocation = GetShortObjLabel(currentParentDBObj);
    if (isOrphanChange(change)) {
      curLocation = appstr("Staging");
      if (doc.IsPositionDocument()) {
        curLocation = appstr("Unassigned").toLocaleLowerCase();
      }
    }

    return (
      <OverflowTooltipWrapper
        tooltipProps={{
          message: appstr("Moved from") + " " + prevParent + " " + appstr("to") + " " + curLocation
        }}
      >
        {ref => (
          <Flex ref={ref} gap={"2px"} alignItems={"flex-end"} className={"oc-text-truncate--2"}>
            <Text size={"xsmall"}>{appstr("Moved from")}</Text>
            <RecordLink dbObj={originalParentDBObj} docRef={doc} />
            <Text size={"xsmall"}>{appstr("to")}</Text>
            <RecordLink change={change} dbObj={currentParentDBObj} docRef={doc} />
          </Flex>
        )}
      </OverflowTooltipWrapper>
    );
  };

  const GetChangedFieldContent = (fieldDef, value) => {
    const fieldName = fieldDef.GetFieldName();
    const isAuthorBlocked = change.IsAuthorBlockedField(fieldName);

    let fromLabel, hasOriginal, isMultiple;

    if (allChanges?.length > 1) {
      const groupResult = resolveGroupPrior(fieldDef, allChanges, { wsMgr, objType });
      fromLabel = groupResult.fromLabel;
      hasOriginal = groupResult.hasOriginal;
      isMultiple = groupResult.isMultiple;
    } else {
      const originalFormatted = getFormattedOriginalValue(fieldDef, ctx);
      hasOriginal = originalFormatted != null && (isAuthorBlocked || originalFormatted !== value);
      fromLabel = originalFormatted;
      isMultiple = false;
    }

    const showFrom = hasOriginal || isMultiple;
    const displayFromLabel = isMultiple ? `(${appstr("multiple values")})` : fromLabel;

    const dbObj = change.fDBObj;
    const recordId = dbObj?.GetObjectRecordID();
    const cloneBase =
      wsMgr?.fPlanManager?.GetCacheBaseRecord(objType, recordId) ??
      dbObj?.fCloneBase ??
      change.fParentOverride?.SmartGetFieldValue?.("displayObject") ??
      null;
    const baseRaw = cloneBase?.GetFieldValue(fieldDef, false) ?? null;
    const isViewerBlocked = baseRaw === kBlockedFieldTag;
    const showBlockedIcon = isAuthorBlocked && !isViewerBlocked;

    const changeDescription = showFrom
      ? `${fieldName} ${appstr("changed from")} ${displayFromLabel} ${appstr("to")} ${value}`
      : `${fieldName} ${appstr("changed to")} ${value}`;

    const blockedTooltipRef = { current: null };

    const blockedIcon = (
      <TooltipWrapper
        fitReferenceToContent
        tooltipProps={{
          openCtrlRef: blockedTooltipRef,
          message: appstr("authorBlockedFieldTip", { fieldName }),
          popoverProps: {
            referenceProps: { style: { verticalAlign: "middle" } }
          }
        }}
      >
        <Icon name="circle-info" size="xsmall" style={{ margin: "0 2px", cursor: "default" }} />
      </TooltipWrapper>
    );

    const numericNowrap = val => (isNumericValue(val) ? { whiteSpace: "nowrap" } : undefined);

    return (
      <OverflowTooltipWrapper
        tooltipProps={{
          referenceOpenCtrlRef: showBlockedIcon ? blockedTooltipRef : undefined,
          message: changeDescription
        }}
      >
        {ref => (
          <Flex ref={ref} className={"oc-text-truncate--2"}>
            <Text size={"xsmall"} modifier={"bold"}>
              {fieldName}
            </Text>
            {showFrom ? (
              <>
                <Text size={"xsmall"} colorModifier={"grey"}>
                  {` ${appstr("changed from")} `}
                </Text>
                {showBlockedIcon ? (
                  <NoBreak>
                    {blockedIcon}
                    <Text size={"xsmall"} modifier={"bold"}>
                      {displayFromLabel}
                    </Text>
                  </NoBreak>
                ) : (
                  <Text size={"xsmall"} modifier={"bold"} style={numericNowrap(displayFromLabel)}>
                    {displayFromLabel}
                  </Text>
                )}
                <Text size={"xsmall"} colorModifier={"grey"}>
                  {` ${appstr("to")} `}
                </Text>
              </>
            ) : (
              <Text size={"xsmall"} colorModifier={"grey"}>
                {` ${appstr("changed to")} `}
              </Text>
            )}
            {!showFrom && showBlockedIcon ? (
              <NoBreak>
                {blockedIcon}
                <Text size={"xsmall"} modifier={"bold"}>
                  {value}
                </Text>
              </NoBreak>
            ) : (
              <Text size={"xsmall"} modifier={"bold"} style={numericNowrap(value)}>
                {value}
              </Text>
            )}
          </Flex>
        )}
      </OverflowTooltipWrapper>
    );
  };

  const GetPositionStatusChangeContent = (isClose, parentDBObj) => {
    const parentLabel = GetShortObjLabel(parentDBObj);
    const changeText = appstr(
      isClose
        ? enumPositionChangeText[enumObjectTypes.position][enumOverrideType.delete]
        : "Reopened under"
    );

    return (
      <OverflowTooltipWrapper tooltipProps={{ message: changeText + " " + parentLabel }}>
        {ref => (
          <Flex ref={ref} gap={"2px"} alignItems={"flex-end"} className={"oc-text-truncate--2"}>
            <Text size={"xsmall"}>{changeText}</Text>
            <RecordLink dbObj={parentDBObj} docRef={doc} />
          </Flex>
        )}
      </OverflowTooltipWrapper>
    );
  };

  const ShouldShowDivider = (displayRows, rowIndex) => {
    return displayRows.length > 1 && rowIndex < displayRows.length - 1;
  };

  const GetChangesDisplayContent = (
    fieldDef,
    displayValue,
    rowIndex,
    displayRows,
    shouldShowDivider
  ) => {
    const fieldName = fieldDef?.GetFieldName();

    const showDivider = shouldShowDivider ?? ShouldShowDivider(displayRows, rowIndex);
    if (
      fieldName === parentIDFieldName &&
      fieldDef.GetFieldClassName() === hierarchyClass.GetClassName()
    ) {
      if (hasParentChanged(changeType, { originalParentDBObj, currentParentDBObj, doc })) {
        return (
          <Fragment key={fieldName}>
            {GetMoveContents()}
            {showDivider && <Divider />}
          </Fragment>
        );
      } else if (changeType === enumOverrideType.add) {
        return (
          <Fragment key={fieldName}>
            {GetChangedReportsContent(currentParentDBObj)}
            {showDivider && <Divider />}
          </Fragment>
        );
      }
    }

    if (
      changeType === enumOverrideType.add &&
      fieldName === containerPositionFieldName &&
      fieldDef.GetFieldClassName() !== hierarchyClass.GetClassName()
    ) {
      return (
        <Fragment key={fieldName}>
          {GetChangedReportsContent(currentParentDBObj)}
          {showDivider && <Divider />}
        </Fragment>
      );
    }

    return (
      <Fragment key={fieldName}>
        {GetChangedFieldContent(fieldDef, displayValue)}
        {showDivider && <Divider />}
      </Fragment>
    );
  };

  const GetDisplayContent = () => {
    const isPlanPositionChart = (doc.IsPlanChart() || tableView) && doc.IsPositionDocument();
    let content = null;
    switch (changeType) {
      case enumOverrideType.add:
      case enumOverrideType.move:
      case enumOverrideType.change:
        if (isPlanPositionChart) {
          const { isChange } = CheckForPositionStatusChange({
            doc,
            tableView,
            change,
            wsMgr,
            objType
          });
          if (isChange) {
            content = GetPositionStatusChangeContentForPlanChart();
            break;
          }
        }

        if (isPlanPositionChart) {
          const { isChange } = CheckForEmployeeAssignmentChange(ctx);
          if (isChange) {
            content = GetEmployeeAssignmentChangeContentForPlanChart();
            break;
          }
        }

        {
          const filteredRows = filterUnchangedRows(getFilteredRows(rows), ctx);
          content = (
            <Flex direction={"column"} gap={"4px"}>
              {renderFilteredRows(filteredRows)}
            </Flex>
          );
        }
        break;
      case enumOverrideType.delete:
        if (isPlanPositionChart) {
          const { isChange } = CheckForEmployeeAssignmentChange(ctx);
          if (isChange) {
            content = GetEmployeeAssignmentChangeContentForPlanChart();
            break;
          }
          content = GetPositionStatusChangeContentForPlanChart();
        } else {
          content = GetDeleteContent(currentParentDBObj);
        }
        break;
      case enumOverrideType.moveout:
        content = GetMoveContents();
        break;
      case enumOverrideType.comment:
        content = (
          <Text size={"xsmall"} modifier={"italic"}>
            {'"' + change.GetChangeComment() + '"'}
          </Text>
        );
        break;
    }
    return content;
  };

  const comment = change.GetChangeComment();

  return (
    <Flex gap={"4px"} alignItems={"flex-start"}>
      {GetDisplayContent()}
      <div className={"oc-right-justify oc-shrink-0"}>
        {showComment && comment !== appstr("No comments") && <CommentPopover change={change} />}
      </div>
    </Flex>
  );
};
